import { TrendingUp, TrendingDown, Minus, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type Props = {
  label: string;
  value: string;
  delta?: number;
  deltaLabel?: string;
  icon?: LucideIcon;
  accent?: boolean;
};

export function KpiCard({ label, value, delta, deltaLabel, icon: Icon, accent }: Props) {
  const direction = delta === undefined ? 'flat' : delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const DeltaIcon = direction === 'up' ? TrendingUp : direction === 'down' ? TrendingDown : Minus;

  return (
    <div className={cn(
      'relative panel p-6 overflow-hidden group transition-colors duration-300',
      accent && 'border-brass-400/30',
      'hover:border-border-accent',
    )}>
      {/* Accent corner mark */}
      <div className="absolute top-0 left-0 w-8 h-px bg-brass-400/60" />
      <div className="absolute top-0 left-0 h-8 w-px bg-brass-400/60" />

      <div className="flex items-start justify-between mb-8">
        <div className="kicker">{label}</div>
        {Icon && (
          <div className="text-text-dim group-hover:text-brass-300 transition-colors">
            <Icon size={16} />
          </div>
        )}
      </div>

      <div className="font-display text-4xl text-text tabular leading-none mb-3">
        {value}
      </div>

      {delta !== undefined && (
        <div className="flex items-center gap-2 text-xs">
          <span className={cn(
            'inline-flex items-center gap-1 tabular',
            direction === 'up'   && 'text-success',
            direction === 'down' && 'text-danger',
            direction === 'flat' && 'text-text-dim',
          )}>
            <DeltaIcon size={12} />
            {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
          </span>
          {deltaLabel && <span className="text-text-dim">{deltaLabel}</span>}
        </div>
      )}
    </div>
  );
}
